/**
 * Standard Handle Configurations for Genie Nodes
 *
 * Standard handle definitions for each node type.
 * Can be customized with override functionality.
 */

import type { Node } from '@xyflow/react'
import type { HandleMeta } from '../types'
import { ensureInpaintHandles } from '@genieartinc/schemas/pools/ai'
import { getSketchById } from '../shader/library'
import {
  SHADER_INPUT_DATA_TYPES,
  SHADER_INPUT_HANDLE_IDS,
  SHADER_INPUT_LABELS,
  SHADER_INPUT_TYPES,
  isShaderInputType,
  type ShaderInputType,
} from '../shader/media/contract'

const SHADER_OUTPUT_HANDLE: HandleMeta = {
  id: 'image-output',
  type: 'output',
  position: { side: 'right', offsetY: 0.5 },
  dataType: 'image',
  label: 'Out',
}

/** Inputs a shader with code but no declared inputs gets: the image input every shader had before inputs were declared. */
const LEGACY_SHADER_INPUTS: readonly ShaderInputType[] = ['image']

export interface ShaderHandleOptions {
  /** Library sketch applied to the node, if any. */
  sketchId?: string
  /** Inputs the current code reads (`state.inputTypes`); wins over the sketch's. */
  inputTypes?: readonly unknown[]
  /** Inputs that currently have an edge. Their handles always stay, so no edge is orphaned. */
  connected?: readonly ShaderInputType[]
  hasCode: boolean
}

/**
 * Inputs a shader node declares: its own `inputTypes` (set by generation or the
 * settings panel), else the applied library sketch's, else the legacy image input.
 */
export function declaredShaderInputs(sketchId: string | undefined, inputTypes: readonly unknown[] | undefined): ShaderInputType[] {
  if (Array.isArray(inputTypes)) return inputTypes.filter(isShaderInputType)
  const sketch = sketchId ? getSketchById(sketchId) : undefined
  if (sketch) return sketch.inputTypes.filter(isShaderInputType)
  return [...LEGACY_SHADER_INPUTS]
}

/**
 * Handles for a shader node. An empty node offers every input so media can be
 * plugged in before prompting; a node with code shows the inputs it declares
 * plus any that are wired. Inputs stack down the left in `SHADER_INPUT_TYPES` order.
 */
export function shaderHandles({ sketchId, inputTypes, connected = [], hasCode }: ShaderHandleOptions): HandleMeta[] {
  const shown = new Set<ShaderInputType>(hasCode ? declaredShaderInputs(sketchId, inputTypes) : SHADER_INPUT_TYPES)
  for (const type of connected) shown.add(type)

  const inputs = SHADER_INPUT_TYPES.filter((type) => shown.has(type)).map<HandleMeta>((type, index) => ({
    id: SHADER_INPUT_HANDLE_IDS[type],
    type: 'input',
    position: { side: 'left', ordered: index },
    dataType: SHADER_INPUT_DATA_TYPES[type],
    maxConnections: 1,
    label: SHADER_INPUT_LABELS[type],
  }))

  return [...inputs, SHADER_OUTPUT_HANDLE]
}

/** @deprecated Use `shaderHandles`; kept for callers that only know the sketch. */
export function shaderHandlesForSketch(sketchId: string | undefined): HandleMeta[] {
  return shaderHandles({ sketchId, hasCode: Boolean(sketchId) })
}

/** The shader input a target handle id belongs to, if it is one. */
export function shaderInputForHandle(handleId: string | null | undefined): ShaderInputType | undefined {
  return SHADER_INPUT_TYPES.find((type) => SHADER_INPUT_HANDLE_IDS[type] === handleId)
}

/**
 * Standard handle definitions per node type
 * Based on Magic Canvas dataType system (text, image, video, code, number, any)
 */
export const STANDARD_HANDLES: Record<string, HandleMeta[]> = {
  FrameInput: [{ id: 'image-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'image', label: 'Image' }],
  FrameOutput: [{ id: 'image-input', type: 'input', position: { side: 'left', offsetY: 0.5 }, dataType: 'image', label: 'Image', maxConnections: 1 }],
  TextFile: [
    {
      id: 'text-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'text',
    },
    {
      id: 'parent-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'text',
      locked: true,
    },
  ],

  ImageFile: [
    {
      id: 'image-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
    {
      id: 'parent-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'image',
      locked: true,
    },
  ],

  AudioFile: [{ id: 'audio-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'audio', label: 'Audio' }],
  VideoFile: [
    {
      id: 'video-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'video',
    },
    {
      id: 'parent-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'video',
      locked: true,
    },
  ],

  VideoNode: [
    {
      id: 'prompt-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'text',
    },
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'image',
    },
    {
      id: 'generated-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'video',
    },
  ],

  ImageNode: [],

  LoraModel: [
    {
      // No offsetY — let NodeHandles distribute proportionally to the actual
      // node height (offsetY is relative to NODE_SIZE=500 and escapes smaller cards).
      id: 'loras-output',
      type: 'output',
      position: { side: 'right' },
      dataType: 'lora',
      label: 'LoRA',
    },
  ],

  // TextNode handles defined in createTextNode factory (no standard handles)

  // An empty shader offers every input; ShaderNodeNode narrows them once it has code.
  ShaderNode: shaderHandles({ hasCode: false }),

  Node3D: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'image',
    },
    {
      id: '3d-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'any',
    },
  ],

  // ============================================================================
  // Data / Series Nodes
  // ============================================================================

  DataTable: [
    {
      id: 'data-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'table' },
    },
  ],

  VariationGenerator: [
    {
      id: 'data-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'table' },
    },
    {
      id: 'variations-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'shuffle' },
    },
  ],

  PromptComposer: [
    {
      id: 'variations-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'any',
      metadata: { icon: 'shuffle' },
    },
    {
      id: 'system-prompt-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'text',
      label: 'System Prompt',
    },
    {
      id: 'prompts-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'text',
    },
  ],

  BatchGenerator: [
    {
      id: 'prompts-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'any',
      metadata: { icon: 'type' },
    },
    {
      id: 'config-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'any',
      metadata: { icon: 'settings' },
    },
    {
      id: 'gallery-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'image' },
    },
  ],

  GalleryGroup: [
    {
      id: 'gallery-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'image' },
    },
  ],

  GalleryContainer: [
    {
      id: 'gallery-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'image' },
    },
    {
      id: 'gallery-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'any',
      metadata: { icon: 'image' },
    },
  ],

  // ============================================================================
  // Utility / Processing Nodes
  // ============================================================================

  ControlNetNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'image',
    },
    {
      id: 'controlnet-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],

  CropNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'image',
    },
    {
      id: 'video-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'video',
    },
    {
      id: 'crop-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],

  ExtractVideoFrameNode: [
    {
      id: 'video-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'video',
    },
    {
      id: 'frame-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],

  LevelsNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'image',
    },
    {
      id: 'video-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'video',
    },
    {
      id: 'levels-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],


  OutpaintNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'image',
    },
    {
      id: 'video-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'video',
    },
    {
      id: 'outpaint-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],

  MaskNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.5 },
      dataType: 'image',
      label: 'Image',
    },
    {
      id: 'mask-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.5 },
      dataType: 'mask',
      label: 'Mask',
    },
  ],

  BlurNode: [
    {
      id: 'prompt-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'text',
    },
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'image',
    },
    {
      id: 'blur-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],



  BackgroundRemoverNode: [
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', offsetY: 0.11 },
      dataType: 'image',
    },
    {
      id: 'bgremove-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: 'image',
    },
  ],


  ThreeDNode: [
    {
      id: 'text-input',
      type: 'input',
      position: { side: 'left', ordered: 0 },
      dataType: 'text',
    },
    {
      id: 'image-input',
      type: 'input',
      position: { side: 'left', ordered: 1 },
      dataType: 'image',
    },
    {
      id: '3d-input',
      type: 'input',
      position: { side: 'left', ordered: 2 },
      dataType: '3d',
    },
    {
      id: '3d-output',
      type: 'output',
      position: { side: 'right', offsetY: 0.11 },
      dataType: '3d',
    },
  ],
}

/**
 * Get handles for a node type with optional overrides
 *
 * @param nodeType - Node type identifier
 * @param overrides - Optional handle overrides (partial handles)
 * @returns Array of handles
 *
 * @example
 * // Standard handles
 * getHandles('TextFile')
 *
 * @example
 * // With override - custom color
 * getHandles('TextFile', [
 *   { id: 'text-output', color: '#ff0000' }
 * ])
 *
 * @example
 * // With override - additional handle
 * getHandles('TextFile', [
 *   { id: 'metadata-output', type: 'output', position: 'bottom', dataType: 'json' }
 * ])
 */
export function getHandles(nodeType: string, overrides?: Partial<HandleMeta>[]): HandleMeta[] {
  const standardHandles = STANDARD_HANDLES[nodeType] || []

  if (!overrides || overrides.length === 0) {
    return standardHandles
  }

  // Apply overrides
  const handleMap = new Map(standardHandles.map((handle) => [handle.id, { ...handle }]))

  overrides.forEach((override) => {
    if (!override.id) return

    if (handleMap.has(override.id)) {
      // Update existing handle
      const existing = handleMap.get(override.id)!
      handleMap.set(override.id, { ...existing, ...override } as HandleMeta)
    } else {
      // Add new handle (must have all required fields)
      if (override.type && override.position && override.dataType !== undefined) {
        handleMap.set(override.id, override as HandleMeta)
      }
    }
  })

  return Array.from(handleMap.values())
}

/**
 * Data type to color mapping
 * Magic Canvas automatically applies colors based on dataType
 * These are reference colors for documentation
 */
export const DATA_TYPE_COLORS: Record<string, string> = {
  text: '#3b82f6', // blue
  image: '#22c55e', // green
  video: '#ef4444', // red (matches scaffold video color)
  audio: '#f97316', // orange
  mask: '#14b8a6', // teal
  '3d': '#8b5cf6', // violet
  code: '#f59e0b', // amber
  number: '#ec4899', // pink
  any: '#6b7280', // gray
}

/**
 * Get color for a data type
 */
export function getDataTypeColor(dataType: string): string {
  return DATA_TYPE_COLORS[dataType] || DATA_TYPE_COLORS.any
}

// ============================================================================
// Purpose-Based Handle Definitions (for AI nodes)
// ============================================================================

/**
 * Purpose-based handle definitions for nodes that vary by purpose.
 * Consolidates definitions previously scattered across individual component files.
 */
export const PURPOSE_HANDLES: Record<string, Record<string, HandleMeta[]>> = {
  ImageNode: {
    create: [
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'text', label: 'Prompt' },
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'image', label: 'Reference' },
      { id: 'loras-input', type: 'input', position: { side: 'left', ordered: 2 }, dataType: 'lora', label: 'LoRAs', maxConnections: 4 },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'image', locked: true },
    ],
    edit: [
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'image', label: 'Image' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Edit Prompt' },
      { id: 'loras-input', type: 'input', position: { side: 'left', ordered: 2 }, dataType: 'lora', label: 'LoRAs', maxConnections: 4 },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'image', locked: true },
    ],
    enhance: [
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'image', label: 'Image' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Instructions' },
      { id: 'loras-input', type: 'input', position: { side: 'left', ordered: 2 }, dataType: 'lora', label: 'LoRAs', maxConnections: 4 },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'image', locked: true },
    ],
    tools: [
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'image', label: 'Image' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Instructions' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'image', locked: true },
    ],
  },

  VideoNode: {
    create: [
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'text', label: 'Prompt' },
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'image', label: 'Reference Frame' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'video', locked: true },
    ],
    edit: [
      { id: 'video-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'video', label: 'Video' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Edit Prompt' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'video', locked: true },
    ],
    enhance: [
      { id: 'video-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'video', label: 'Video' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Instructions' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'video', locked: true },
    ],
    tools: [
      { id: 'video-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'video', label: 'Video' },
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'text', label: 'Instructions' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'video', locked: true },
    ],
  },

  TextNode: {
    default: [
      { id: 'prompt-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'text', label: 'Prompt' },
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'image', label: 'Image Reference' },
      { id: 'generated-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'text', locked: true },
    ],
  },

  ThreeDNode: {
    default: [
      { id: 'text-input', type: 'input', position: { side: 'left', ordered: 0 }, dataType: 'text', label: 'Text' },
      { id: 'image-input', type: 'input', position: { side: 'left', ordered: 1 }, dataType: 'image', label: 'Image' },
      { id: '3d-input', type: 'input', position: { side: 'left', ordered: 2 }, dataType: '3d', label: '3D Model' },
      { id: '3d-output', type: 'output', position: { side: 'right', offsetY: 0.11 }, dataType: '3d' },
    ],
  },

  MaskNode: {
    default: [
      { id: 'image-input', type: 'input', position: { side: 'left', offsetY: 0.5 }, dataType: 'image', label: 'Image' },
      { id: 'mask-output', type: 'output', position: { side: 'right', offsetY: 0.5 }, dataType: 'mask', label: 'Mask' },
    ],
  },
}

// ============================================================================
// Handle Resolution (for validator + visual feedback)
// ============================================================================

/**
 * Resolve handles for a React Flow node.
 *
 * Priority:
 * 1. node.data.handles (persisted/dynamic handles)
 * 2. STANDARD_HANDLES[componentType] (static defaults)
 * 3. PURPOSE_HANDLES[componentType][purpose] (purpose-based AI nodes)
 * 4. Empty array (unknown node type)
 */
export function resolveHandlesForNode(node: Node): HandleMeta[] {
  const data = node.data as Record<string, unknown> | undefined
  const componentType = node.type || ''
  const persistedHandles = data?.handles as HandleMeta[] | undefined

  // For AI nodes with purpose-based schemas, merge persisted with current purpose handles
  // so schema additions (e.g. 'loras-input') appear on nodes saved before the handle existed.
  const purposeMap = PURPOSE_HANDLES[componentType]
  if (purposeMap) {
    const state = data?.state as Record<string, unknown> | undefined
    const purpose = (state?.purpose as string) || 'create'
    const purposeHandles = purposeMap[purpose]
      || purposeMap['default']
      || purposeMap['create']
      || []
    const withPurpose = !persistedHandles || persistedHandles.length === 0
      ? purposeHandles
      : (() => {
          const existingIds = new Set(persistedHandles.map((h) => h.id))
          const missing = purposeHandles.filter((h) => !existingIds.has(h.id))
          return missing.length === 0 ? persistedHandles : [...persistedHandles, ...missing]
        })()
    return ensureInpaintHandles(withPurpose, state?.subPurpose as string | undefined)
  }

  // Shader handles are derived, not persisted (see ShaderNodeNode). Every input
  // a shader can grow is resolvable, so connections validate against the
  // handle the user actually sees rather than a stale persisted list.
  if (componentType === 'ShaderNode') return shaderHandles({ hasCode: false })

  // Non-purpose nodes: persisted wins, else STANDARD_HANDLES, else empty
  if (persistedHandles && persistedHandles.length > 0) return persistedHandles
  const standard = STANDARD_HANDLES[componentType]
  if (standard && standard.length > 0) return standard

  return []
}
